import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Upload, X, FileIcon, ImageIcon, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const categories = ['digital', 'template', 'course', 'ebook', 'art', 'music', 'software', 'other'];

export default function CreateProductPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('digital');
  const [files, setFiles] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const filesRef = useRef(null);
  const previewRef = useRef(null);

  const handleFilesSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...selected]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handlePreviewSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreviewImage(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !price || files.length === 0) {
      toast.error('Please fill title, price, and upload at least one file');
      return;
    }
    setUploading(true);
    setProgress(10);
    try {
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('description', description.trim());
      formData.append('price', parseFloat(price).toString());
      formData.append('category', category);
      for (const file of files) {
        formData.append('files', file);
      }
      if (previewImage) {
        formData.append('preview_image', previewImage);
      }
      setProgress(30);
      await api.post('/products/create', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          const pct = 30 + Math.round((e.loaded / e.total) * 60);
          setProgress(Math.min(pct, 90));
        }
      });
      setProgress(100);
      toast.success('Product created successfully');
      setTimeout(() => navigate('/marketplace'), 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create product');
    } finally {
      setUploading(false);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="max-w-2xl mx-auto animate-in" data-testid="create-product-page">
      <h1 className="font-serif text-2xl md:text-3xl mb-2">Create Product</h1>
      <p className="text-sm text-muted-foreground mb-8">List a digital product with downloadable files for buyers</p>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Title */}
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Product Title</Label>
          <Input
            data-testid="create-product-title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Premium Creator Bundle"
            required
            className="rounded-none border-0 border-b border-input bg-transparent px-0 py-2 shadow-none focus-visible:ring-0 focus-visible:border-obsidian text-lg font-serif mt-1"
          />
        </div>

        {/* Description */}
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Description</Label>
          <Textarea
            data-testid="create-product-description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Describe what buyers will receive..."
            rows={4}
            className="rounded-none border-0 border-b border-input bg-transparent px-0 py-2 shadow-none focus-visible:ring-0 resize-none text-sm mt-1"
          />
        </div>

        {/* Price + Category */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Price (EUR)</Label>
            <Input
              data-testid="create-product-price"
              type="number"
              step="0.01"
              min="0.50"
              value={price}
              onChange={e => setPrice(e.target.value)}
              placeholder="299.00"
              required
              className="rounded-none border-0 border-b border-input bg-transparent px-0 py-2 shadow-none focus-visible:ring-0 mt-1"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="create-product-category" className="rounded-sm mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-sm">
                {categories.map(c => (
                  <SelectItem key={c} value={c} className="text-xs capitalize">{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Preview Image */}
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Preview Image (optional)</Label>
          <div
            onClick={() => previewRef.current?.click()}
            className="border-2 border-dashed border-border/60 hover:border-gold/50 transition-colors cursor-pointer p-6 text-center"
          >
            <input ref={previewRef} type="file" accept="image/*" className="hidden" onChange={handlePreviewSelect} data-testid="create-product-preview-input" />
            {previewUrl ? (
              <div className="relative inline-block">
                <img src={previewUrl} alt="Preview" className="max-h-32 object-cover rounded-sm" />
                <button type="button" onClick={(e) => { e.stopPropagation(); setPreviewImage(null); setPreviewUrl(''); }} className="absolute -top-2 -right-2 bg-obsidian text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">&times;</button>
              </div>
            ) : (
              <>
                <ImageIcon size={24} className="mx-auto text-muted-foreground/30 mb-2" />
                <p className="text-xs text-muted-foreground">Click to add a preview image</p>
              </>
            )}
          </div>
        </div>

        {/* Downloadable Files */}
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Downloadable Files</Label>
          <div
            data-testid="create-product-files-dropzone"
            onClick={() => filesRef.current?.click()}
            className="border-2 border-dashed border-border/60 hover:border-gold/50 transition-colors cursor-pointer p-8 text-center"
          >
            <input ref={filesRef} type="file" multiple className="hidden" onChange={handleFilesSelect} data-testid="create-product-files-input" />
            <Upload size={28} className="mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm font-medium">Click to select files for buyers</p>
            <p className="text-xs text-muted-foreground mt-1">PDFs, images, documents, resources - buyers will download these</p>
          </div>

          {files.length > 0 && (
            <div className="mt-4 space-y-2">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-3 border border-border/40 p-3 group" data-testid={`product-file-${i}`}>
                  <FileIcon size={16} className="text-gold shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{f.name}</p>
                    <p className="text-[10px] text-muted-foreground">{formatSize(f.size)} &middot; {f.type || 'unknown'}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    data-testid={`remove-file-${i}`}
                    className="text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              <p className="text-xs text-muted-foreground">{files.length} file(s) ready to upload</p>
            </div>
          )}
        </div>

        {/* Upload Progress */}
        {uploading && (
          <div>
            <Progress value={progress} className="h-1 rounded-none" />
            <p className="text-xs text-muted-foreground mt-2 text-center">{progress}%</p>
          </div>
        )}

        {/* Submit */}
        <Button
          type="submit"
          data-testid="create-product-submit"
          disabled={uploading || !title.trim() || !price || files.length === 0}
          className="w-full rounded-sm h-12 uppercase tracking-wider text-xs bg-obsidian hover:bg-gold hover:text-obsidian transition-colors duration-300"
        >
          {uploading ? (
            <><Loader2 size={14} className="mr-2 animate-spin" /> Creating Product...</>
          ) : (
            <><CheckCircle size={14} className="mr-2" /> Create Product</>
          )}
        </Button>
      </form>
    </div>
  );
}
